<template lang="pug">
#home
  .hero.is-large(v-if="!isMobile")
    video(poster="/video/cwo-desktop-alt-poster.jpg" autoplay muted loop)
      source(src="/video/cwo-desktop-alt.mp4")
    .hero-wrapper
      .container.mobile-padding(v-parallax="-0.2")
        h1
          span Reaching the world,
          br
          span one person at a time.
        h2 {{ account.organization }}
      scroll-indicator(v-if="!isMobile")
  .description-content-block.content-block
    .container.mobile-padding
      .columns
        .column.is-half
          img.is-hidden-tablet.crest(src="/images/cwo/cwo-crest.png")
          h3.is-size-2-desktop.is-size-3-touch.is-uppercase
            span(v-if="isMobile") Reaching the world,<br/>one person</br>at a time.
            span(v-else) At Cornerstone,</br>we cater to an audience</br>of One.
        .column.is-half
          .content.is-size-5
            p Cornerstone World Outreach is a Christian congregation in the heartland of the nation. We’re bold, passionate, and serious about making disciples.
            p Our highest goal is to honor God Almighty through obedience and conformity to His Word. <i>We&rsquo;re reaching the world one person at a time,</i> but the person we want to please most just happens to live in Heaven.
            p Are you ready to be spiritually challenged? Welcome to Cornerstone.

  home-features

  spinner(v-if="episodes.length === 0")
  .episodes-content-block.content-block(v-else)
    .container
      .mobile-padding.is-hidden-desktop
        h3.is-size-4.is-uppercase Recent Sermons
      .columns.is-multiline
        .column.is-one-quarter(
          v-for="episode in episodes"
          :key="episode.id"
        )
          episode-list-item(
            :hoverable="true"
            :value="episode"
          )
      .view-more
        router-link.button.is-primary(
          :to="{ name: 'services', query: { tab: 'sermons' } }"
        )
          span View More Services
          b-icon(icon="arrow-right" size="is-small")

  .hero(style="background-image:url('/images/cwo/hero10.jpg')")
    .hero-quote.container.mobile-padding(v-parallax="-0.2")
      h1 &ldquo;Christ is the head of the Church...that in everything He might be preeminent.&rdquo;<br/><small>&mdash; Colossians 1:18 &mdash;</small>

  .map-block.content-block
    .container
      .columns
        .column.is-one-third
          .mobile-padding
            h3.is-size-4.is-uppercase.no-bottom-margin Upcoming Events
          .events-column
            div(v-if="events.length === 0")
              p.has-text-grey-light No upcoming events
            div(
              v-else
              v-for="event in events"
              :key="event.id"
            )
              event-list-item(
                :direction="isMobile ? 'vertical' : 'horizontal'"
                :limit="2"
                :value="event"
              )
        .column.is-one-third
          .mobile-padding
            h3.is-size-4.is-uppercase.no-bottom-margin Recent Series
          .collection-column
            div(
              v-for="collection in collections"
              :key="collection.id"
            )
              collection-list-item(
                :direction="isMobile ? 'vertical' : 'horizontal'"
                :value="collection"
              )
        .column.is-one-third
          .mobile-padding
            h3.is-size-4.is-uppercase Service Times
            service-list-item(
              v-for="service in account._embedded.services"
              :key="service.id"
              :value="service"
            )
</template>

<script>
import { mapGetters } from 'vuex';
import _ from 'lodash';
import moment from 'moment';
import { SEARCH } from '@/utils/http';
import ArticleListItem from '@/components/articles/ArticleListItem.vue';
import CollectionListItem from '@/components/collections/CollectionListItem.vue';
import EpisodeListItem from '@/components/episodes/EpisodeListItem.vue';
import EventListItem from '@/components/events/EventListItem.vue';
import GoogleMap from '@/components/GoogleMap.vue';
import HomeFeatures from '@/components/HomeFeatures.vue';
import ScrollIndicator from '@/components/ScrollIndicator.vue';
import ServiceListItem from '@/components/ministries/ServiceListItem.vue';
import Spinner from '@/components/Spinner.vue';
import * as getterTypes from '@/store/types/getters';

export default {
  name: 'home',
  components: {
    ArticleListItem,
    CollectionListItem,
    EpisodeListItem,
    EventListItem,
    GoogleMap,
    HomeFeatures,
    ScrollIndicator,
    ServiceListItem,
    Spinner,
  },
  computed: {
    ...mapGetters({
      account: getterTypes.GET_ACCOUNT,
    }),
    articlesQuery() {
      const payload = {
        size: 6,
        sort: [
          { pub_date: 'desc' },
          { 'title.raw': { order: 'asc' } },
          '_score',
        ],
        query: {
          bool: {
            must: [
              { match: { account_id: this.account.id } },
              { match: { is_published: true } },
              { range: { pub_date: { lte: this.today } } },
            ],
          },
        },
      };
      return payload;
    },
    collectionsQuery() {
      const payload = {
        size: 3,
        sort: [
          { is_featured: { order: 'asc' } },
          { start_date: { order: 'desc' } },
          '_score',
        ],
        query: {
          bool: {
            must: [
              { match: { account_id: this.account.id } },
              { range: { episode_count: { gte: 1 } } },
            ],
          },
        },
      };
      return payload;
    },
    episodesQuery() {
      const payload = {
        size: 12,
        sort: [
          { pub_date: { order: 'desc' } },
          '_score',
        ],
        query: {
          bool: {
            must: [
              { match: { account_id: this.account.id } },
            ],
            filter: [
              { term: { is_published: true } },
              { range: { pub_date: { lte: this.today } } },
            ],
          },
        },
      };
      return payload;
    },
    eventsQuery() {
      const payload = {
        size: 3,
        sort: [
          { end_date: { order: 'asc' } },
          { 'title.raw': { order: 'asc' } },
          '_score',
        ],
        query: {
          bool: {
            must: [
              { match: { type: 'event' } },
              { match: { account_id: this.account.id } },
              { match: { is_published: true } },
              { range: { pub_date: { lte: this.today } } },
              { range: { end_date: { gte: this.today } } },
            ],
          },
        },
      };
      return payload;
    },
    isMobile() {
      return window.innerWidth <= 769;
    },
    projectsQuery() {
      const payload = {
        size: 4,
        sort: [
          { pub_date: 'desc' },
          { 'title.raw': { order: 'asc' } },
          '_score',
        ],
        query: {
          bool: {
            must: [
              { match: { account_id: this.account.id } },
              { match: { is_published: true } },
              { range: { pub_date: { lte: this.today } } },
              { match: { type: 'project' } },
            ],
          },
        },
      };
      return payload;
    },
  },
  async created() {
    this.settings.loading = true;
    this.today = moment().format('YYYY-MM-DD');
    this.month = moment().startOf('month').format('YYYY-MM-DD');
    await Promise.all([
      // this.getArticles(),
      this.getCollections(),
      this.getEpisodes(),
      this.getEvents(),
    ]);
    this.settings.loading = false;
  },
  data() {
    return {
      articles: [],
      collections: [],
      episodes: [],
      events: [],
      month: null,
      projects: [],
      settings: {
        loading: false,
      },
      today: null,
    };
  },
  methods: {
    async getArticles() {
      const { data } = await SEARCH.post('/doxa_articles/_search', this.articlesQuery);
      this.articles = data.hits.hits.map(h => h._source);
      return data;
    },
    async getCollections() {
      const { data } = await SEARCH.post('/doxa_episode_collections/_search', this.collectionsQuery);
      this.collections = data.hits.hits.map(h => h._source);
      return data;
    },
    async getEpisodes() {
      const { data } = await SEARCH.post('/doxa_episodes/_search', this.episodesQuery);
      this.episodes = data.hits.hits.map(h => h._source);
      return data;
    },
    async getEvents() {
      const { data } = await SEARCH.post('/doxa_events/_search', this.eventsQuery);
      this.events = this.sortEvents(data.hits.hits.map(h => h._source));
      return data;
    },
    sortEvents(events) {
      const today = moment();
      return _.sortBy(events, (e) => {
        const filtered = _.filter(e.dates, (d) => {
          const date = moment(d, 'YYYY-MM-DD');
          return date.isSameOrAfter(today, 'day');
        });
        return _.min(filtered);
      });
    },
  },
};
</script>

<style theme="cwo" lang="scss">
#home {
  margin-top: calc(#{$navbar-height} * -1);
  video {
    min-width: 100%;
  }
  .hero {
    @include hero;
    &.is-large {
      display: block;
      height: 100vh;
      background-color: transparent;
      video {
        position: absolute;
        top: 50%;
        left: 50%;
        height: 100vh;
        width: 100%;
        z-index: 0;
        transform: translate(-50%, -50%);
        object-fit: cover;
      }
      h1 {
        margin-top: 0.5rem;
        font-size: 4.5rem;
      }
      h2 {
        font-size: 1.75rem;
        text-transform: uppercase;
      }
      h1, h2 {
        text-shadow: 0 0 60px rgba(0,0,0,0.2);
      }
      .hero-wrapper {
        position: relative;
        z-index: 1;
        display: flex;
        flex-flow: column;
        align-items: center;
        justify-content: center;
        height: 100%;
      }
    }
    &:not(.is-large) {
      height: 60vh;
      h1 {
        font-size: 3rem;
      }
    }
    h1 {
      font-family: $body-font;
      font-weight: 700;
    }
    .container {
      align-items: center;
      display: flex;
      flex-flow: column;
      justify-content: center;
    }
    .hero-quote {
      h1 {
        line-height: 150%;
        text-shadow: 0 10px 15px rgba(0,0,0,0.15);
      }
      small {
        font-size: 1.75rem;
      }
    }
    .ctas {
      justify-content: center;
      margin-top: 2.5rem;
      a {
        padding: 0 0.75rem;
        font-size: 1.2rem;
        span {
          border-bottom: 2px solid transparent;
        }
        &:hover {
          color: $primary;
          span {
            border-bottom: 2px solid $primary;
          }
        }
      }
    }
  }
  .content-block {
    margin: 6.5rem 0;
    &.description-content-block {
      .columns {
        display: flex;
        align-items: center;
      }
    }
    .content-block-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      a {
        @include link;
        margin-bottom: 0.75rem;
      }
    }
    h3 {
      font-family: $heading-font;
      font-weight: 500;
      font-size: 1.25rem;
      margin-bottom: 1.25rem;
    }
    .google-map {
      height: 400px;
    }
  }
  .view-more {
    margin-top: 2rem;
    text-align: center;
  }
  .events-column, .collection-column {
    & > div {
      margin-top: 14px;
    }
  }
  @media (min-width: $tablet) {
    .hero {
      .hero-quote {
        max-width: 75%;
      }
    }
  }
  @media (max-width: $tablet) {
    // .hero {
    //   background-attachment: inherit;
    //   background-position: center;
    //   background-size: cover;
    // }
    .hero {
      &.is-large {
        .hero-wrapper {
          background-image: url('/images/cwo/home-mobile.jpg');
          background-size: cover;
        }
      }
      .hero-wrapper {
        height: 60vh;
        background-color: black;
      }
      .container {
        h1 {
          font-size: 2rem;
        }
        h2 {
          font-size: 1.25rem;
        }
      }
    }
    .content-block {
      margin: 2.5rem 0;
      &.episodes-content-block {
        h3 {
          margin-bottom: 0.25rem;
        }
        .columns {
          @include carousel;
        }
      }
      &.description-content-block {
        margin-top: 4.25rem;
        margin-bottom: 4.25rem;
        text-align: center;
        background-image: url('/images/cwo/home-mobile-alt.jpg');
        background-size: contain;
        background-repeat: no-repeat;
        .crest {
          max-width: 50%;
          margin: 1.5rem auto 1.5rem auto;
        }
        .columns {
          h3 {
            margin-bottom: 0;
          }
          p {
            line-height: 175%;
            color: $grey;
          }
          flex-flow: column;
        }
      }
    }
    .no-bottom-margin {
      margin-bottom: 0 !important;
    }
    .events-column, .collection-column {
      @include carousel;
      margin-top: -12px;
      & > div {
        padding: 12px;
        &:first-child {
          padding-left: 20px;
        }
      }
    }
  }
}
</style>
